import { IconFilter, IconSearch } from '@tabler/icons-react'
import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useLabels, useSavedLists, useSourceCatalogue } from '../../api/queries'
import type { PropertyMarkerQuery } from '../../api/types'
import { humanize } from '../../api/format'
import type { ExperienceSpec, FilterKey } from '../../experiences/registry'
import { PickerRow, RangeRow } from '../../ui/kit'
import { LocationRows } from './LocationRows'
import {
  ASSET_CLASS_OPTIONS, BAND_OPTIONS, BUILT_FORM_OPTIONS, DWELLING_OPTIONS, LEGAL_PACK_OPTIONS, SALE_METHOD_OPTIONS, STATUS_OPTIONS, TENURE_OPTIONS, VISITED_OPTIONS,
  countActive, type Filters,
} from './filters'

const ALL = '__all__'

export function FilterRail({ filters, spec, markerQuery, onChange, onReset, savedFilters }: {
  filters: Filters; spec: ExperienceSpec; markerQuery: PropertyMarkerQuery; onChange: (patch: Partial<Filters>) => void; onReset: () => void; savedFilters?: ReactNode
}) {
  const lists = useSavedLists()
  const labels = useLabels()
  const sources = useSourceCatalogue()
  const has = (key: FilterKey) => spec.register.filters.includes(key)
  const active = countActive(filters)
  const sourceOptions = (sources.data ?? []).filter(s => s.sourceKey).map(s => ({ value: s.sourceKey, label: s.name || humanize(s.sourceKey) }))
  const listOptions = [{ value: ALL, label: 'All properties' }, ...(lists.data ?? []).map(l => ({ value: l.id, label: l.name, count: l.propertyCount }))]
  return (
    <div className="rail">
      <div className="rail-head">
        <div className="t"><IconFilter className="ic" aria-hidden="true" /><b>Filters</b><span>· {active === 0 ? 'None set' : `${active} set`}</span></div>
        <button type="button" onClick={onReset}>Reset</button>
      </div>
      {has('q') ? (
        <label className="rail-search"><IconSearch className="ic" aria-hidden="true" /><input value={filters.q} placeholder="Address or postcode" onChange={e => onChange({ q: e.target.value })} aria-label="Search address or postcode" /></label>
      ) : null}

      {has('scope') ? (
        <section className="rail-sec">
          <div className="rail-sh"><span className="t-section">Scope</span><Link to="/lists" className="hint">manage lists</Link></div>
          <PickerRow label="List" scope options={listOptions} value={filters.listId ?? ALL} defaultValue={ALL} clearable={false} searchable={(lists.data?.length ?? 0) > 8}
            onChange={v => onChange({ listId: v === ALL || v === null ? null : (v as string) })}
            footer={<Link to="/lists" className="btn is-small is-accent">+ New list</Link>} />
        </section>
      ) : null}

      {has('savedFilters') ? savedFilters : null}

      {has('location') ? (
        <section className="rail-sec">
          <div className="rail-sh"><span className="t-section">Location</span><span className="hint">place or map area</span></div>
          <LocationRows location={filters} markerQuery={markerQuery} onChange={patch => onChange(patch)} />
        </section>
      ) : null}

      <section className="rail-sec">
        <div className="rail-sh"><span className="t-section">Status &amp; source</span></div>
        {has('status') ? <PickerRow label="Status" options={STATUS_OPTIONS} value={filters.status} defaultValue="active" clearable={false} onChange={v => onChange({ status: (v as Filters['status']) ?? 'active' })} /> : null}
        {has('source') ? <PickerRow label="Source" options={sourceOptions} value={filters.sourceKey} searchable onChange={v => onChange({ sourceKey: (v as string | null) })} /> : null}
        {has('visited') ? <PickerRow label="Visited" options={VISITED_OPTIONS} value={filters.visited === null ? null : String(filters.visited)} onChange={v => onChange({ visited: v === null ? null : v === 'true' })} /> : null}
      </section>

      <section className="rail-sec">
        <div className="rail-sh"><span className="t-section">Property</span></div>
        {has('assetClasses') ? <PickerRow label="Asset class" multi options={ASSET_CLASS_OPTIONS} value={filters.assetClasses} onChange={v => onChange({ assetClasses: v as string[] })} /> : null}
        {has('dwellingTypes') ? <PickerRow label="Dwelling" multi options={DWELLING_OPTIONS} value={filters.dwellingTypes} onChange={v => onChange({ dwellingTypes: v as string[] })} /> : null}
        {has('builtForms') ? <PickerRow label="Built form" multi options={BUILT_FORM_OPTIONS} value={filters.builtForms} onChange={v => onChange({ builtForms: v as string[] })} /> : null}
        {has('tenures') ? <PickerRow label="Tenure" multi options={TENURE_OPTIONS} value={filters.tenures} onChange={v => onChange({ tenures: v as string[] })} /> : null}
        {has('saleMethods') ? <PickerRow label="Sale method" multi options={SALE_METHOD_OPTIONS} value={filters.saleMethods} onChange={v => onChange({ saleMethods: v as string[] })} /> : null}
        {has('beds') ? <RangeRow label="Beds" min={filters.minBedrooms} max={filters.maxBedrooms} onChange={(min, max) => onChange({ minBedrooms: min, maxBedrooms: max })} /> : null}
        {has('price') ? <RangeRow label="Price" format={v => `£${v}k`} min={filters.minPriceK} max={filters.maxPriceK} onChange={(min, max) => onChange({ minPriceK: min, maxPriceK: max })} /> : null}
      </section>

      <section className="rail-sec">
        <div className="rail-sh"><span className="t-section">Evidence</span><span className="hint">bands, not scores</span></div>
        {has('potentialBand') ? <PickerRow label="Resale potential" options={BAND_OPTIONS} value={filters.potentialBand} onChange={v => onChange({ potentialBand: (v as Filters['potentialBand']) })} /> : null}
        {has('legalPack') ? <PickerRow label="Legal pack" options={LEGAL_PACK_OPTIONS} value={filters.hasLegalPack === null ? null : filters.hasLegalPack ? 'with' : 'without'} onChange={v => onChange({ hasLegalPack: v === null ? null : v === 'with' })} /> : null}
        {has('labels') ? <PickerRow label="Labels" multi options={(labels.data ?? []).map(l => ({ value: l.id, label: l.name, count: l.propertyCount }))} value={filters.labelIds} onChange={v => onChange({ labelIds: v as string[] })} /> : null}
      </section>
    </div>
  )
}
